import React, { useState, useEffect } from 'react';
import LeaveService from '../services/leaveService';
import LeaveTypeService from '../services/leaveTypeService';

const LeaveApplicationModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    leaveTypeId: '',
    durationType: 'FULL_DAY',
    startDate: '',
    endDate: '',
    reason: ''
  });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchLeaveTypes();
    }
  }, [isOpen]);

  const fetchLeaveTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await LeaveTypeService.getAllLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
      setError('Failed to load leave types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear end date when switching to half day
    if (name === 'durationType' && value === 'HALF_DAY') {
      setFormData(prev => ({
        ...prev,
        endDate: ''
      }));
    }

    // Set end date same as start date for half day
    if (name === 'startDate' && formData.durationType === 'HALF_DAY') {
      setFormData(prev => ({
        ...prev,
        endDate: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        leaveTypeId: formData.leaveTypeId,
        durationType: formData.durationType,
        startDate: formData.startDate,
        reason: formData.reason.trim()
      };

      // Only include endDate for full day leave
      if (formData.durationType === 'FULL_DAY') {
        submitData.endDate = formData.endDate;
      }

      await LeaveService.applyForLeave(submitData);
      
      // Reset form
      setFormData({
        leaveTypeId: '',
        durationType: 'FULL_DAY',
        startDate: '',
        endDate: '',
        reason: ''
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      setError(error.error || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      leaveTypeId: '',
      durationType: 'FULL_DAY',
      startDate: '',
      endDate: '',
      reason: ''
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Apply for Leave</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {loadingTypes ? (
            <div className="loading-message">Loading leave types...</div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="leaveTypeId">Leave Type *</label>
                <select
                  id="leaveTypeId"
                  name="leaveTypeId"
                  value={formData.leaveTypeId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Leave Type</option>
                  {leaveTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name} ({type.maxLeavesPerYear} days/year)
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="durationType">Duration Type *</label>
                <select
                  id="durationType"
                  name="durationType"
                  value={formData.durationType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="FULL_DAY">Full Day Leave</option>
                  <option value="HALF_DAY">Half Day Leave</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="startDate">
                  {formData.durationType === 'HALF_DAY' ? 'Date' : 'Start Date'} *
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {formData.durationType === 'FULL_DAY' && (
                <div className="form-group">
                  <label htmlFor="endDate">End Date *</label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || today}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="reason">Reason *</label>
                <textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Please provide a reason for your leave request..."
                  rows="4"
                  maxLength="500"
                  required
                />
                <small className="char-count">
                  {formData.reason.length}/500 characters
                </small>
              </div>
            </>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || loadingTypes || !formData.leaveTypeId}
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveApplicationModal;